import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, Select } from '@artosera/ui';
import type { SourceSignature, SignataireSignature } from '@artosera/shared';
import { signaturesApi, useEnvoyerSignature } from '../../api/signatures.js';
import { appelDeRequete, cleMetier } from '../../api/cles.js';
import { Field } from '../Field.js';
import styles from './Signatures.module.css';
export function SignatureFormulaire({
  galerie,
  source,
  fermer,
}: {
  galerie: string;
  source: SourceSignature;
  fermer: () => void;
}) {
  const { t } = useTranslation(['regie', 'commun']);
  const label = (cle: string) => t(`regie:signatures.${cle}`);
  const propose = useQuery({
    queryKey: cleMetier(galerie, 'signatures', 'source', source),
    queryFn: (c) => signaturesApi.source(galerie, source, appelDeRequete(c)),
  });
  const envoyer = useEnvoyerSignature(galerie);
  const [signataires, setSignataires] = useState<SignataireSignature[] | null>(null);
  const [cle] = useState(() => crypto.randomUUID());
  const [niveau, setNiveau] = useState<'simple' | 'avancee'>('simple');
  const [expireLe, setExpireLe] = useState(() =>
    new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 16),
  );
  const [relance, setRelance] = useState('50');
  const [erreur, setErreur] = useState('');
  const valeurs: SignataireSignature[] = signataires ?? [
    {
      nom: propose.data?.galerie[0]?.nom ?? '',
      email: propose.data?.galerie[0]?.email ?? '',
      role: 'galerie',
      ordre: 1,
    },
    {
      nom: propose.data?.tiers[0]?.nom ?? '',
      email: propose.data?.tiers[0]?.email ?? '',
      ...(propose.data?.tiers[0]?.telephone ? { telephone: propose.data.tiers[0].telephone } : {}),
      role: 'tiers',
      ordre: 2,
    },
  ];
  const modifier = (rang: number, changement: Partial<SignataireSignature>) =>
    setSignataires(valeurs.map((s, i) => (i === rang ? { ...s, ...changement } : s)));
  return (
    <Dialog
      open
      onOpenChange={(ouvert) => {
        if (!ouvert && !envoyer.isPending) fermer();
      }}
      title={label('envoyer')}
      closeLabel={t('commun:actions.fermer')}
    >
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          setErreur('');
          void envoyer
            .mutateAsync({
              source,
              cleIdempotence: cle,
              signataires: valeurs,
              niveau,
              expireLe: new Date(expireLe).toISOString(),
              relancePourcentage: relance ? Number(relance) : null,
            })
            .then(fermer)
            .catch(() => setErreur(label('echecEnvoi')));
        }}
      >
        <p>{label('cout')}</p>
        {propose.isPending && <p role="status">{t('commun:chargement')}</p>}
        {propose.isError && <p role="alert">{t('commun:erreurReseau')}</p>}
        {propose.data &&
          valeurs.map((s, rang) => (
            <fieldset key={rang} className={styles.form}>
              <legend>{label(s.role)}</legend>
              <Field htmlFor={`signataire-${rang}`} label={label('nom')}>
                <Input
                  id={`signataire-${rang}`}
                  required
                  value={s.nom}
                  onChange={(e) => modifier(rang, { nom: e.target.value })}
                />
              </Field>
              <Field htmlFor={`email-${rang}`} label={label('email')}>
                <Select
                  id={`email-${rang}`}
                  value={s.email || '__choisir'}
                  options={[
                    { value: '__choisir', label: '—' },
                    ...(propose.data?.[s.role] ?? []).map((p) => ({
                      value: p.email,
                      label: p.email,
                    })),
                  ]}
                  onValueChange={(email) =>
                    modifier(rang, { email: email === '__choisir' ? '' : email })
                  }
                />
              </Field>
              <Field htmlFor={`telephone-${rang}`} label={label('telephone')}>
                <Input
                  id={`telephone-${rang}`}
                  type="tel"
                  required={niveau === 'avancee'}
                  value={s.telephone ?? ''}
                  onChange={(e) => {
                    const telephone = e.target.value;
                    setSignataires(
                      valeurs.map((s, i) => {
                        if (i !== rang) return s;
                        const { telephone: _ancien, ...reste } = s;
                        return telephone ? { ...reste, telephone } : reste;
                      }),
                    );
                  }}
                />
              </Field>
              <Field htmlFor={`ordre-${rang}`} label={label('ordre')}>
                <Input
                  id={`ordre-${rang}`}
                  type="number"
                  min={1}
                  max={10}
                  value={s.ordre}
                  onChange={(e) => modifier(rang, { ordre: Number(e.target.value) })}
                />
              </Field>
            </fieldset>
          ))}
        <Field htmlFor="niveau-signature" label={label('niveau')}>
          <Select
            id="niveau-signature"
            value={niveau}
            onValueChange={(v) => setNiveau(v as 'simple' | 'avancee')}
            options={['simple', 'avancee'].map((value) => ({ value, label: label(value) }))}
          />
        </Field>
        <Field htmlFor="expiration-signature" label={label('expiration')}>
          <Input
            id="expiration-signature"
            type="datetime-local"
            required
            value={expireLe}
            onChange={(e) => setExpireLe(e.target.value)}
          />
        </Field>
        <Field htmlFor="relance-signature" label={label('relancePourcentage')}>
          <Input
            id="relance-signature"
            type="number"
            min={1}
            max={99}
            value={relance}
            onChange={(e) => setRelance(e.target.value)}
          />
        </Field>
        {erreur && <p role="alert">{erreur}</p>}
        <Button
          type="submit"
          loading={envoyer.isPending}
          disabled={!propose.data || valeurs.some((s) => !s.email)}
        >
          {label('confirmerEnvoi')}
        </Button>
      </form>
    </Dialog>
  );
}
